'use client';

import { useAtom, useAtomValue } from 'jotai';
import { usePathname } from 'next/navigation';

import { localeAtom } from '@core/Atoms/Locale/Locale.atom';
import { SegmentedButtons } from '@gds/SegmentedButtons/SegmentedButtons';

import { calculationAtom } from '../../../Atoms/Calculation.atom';
import { currentProductIdAtom } from '../../../Atoms/CurrentProductId.atom';
import { eligibleProductsAtom } from '../../../Atoms/EligibleProducts.atom';
import { finCalcInteractedWithAtom } from '../../../Atoms/FinCalcInteractedWith.atom';
import { selectedProductIdAtom } from '../../../Atoms/SelectedProductId.atom';
import { isLeasingFinancing } from '../../../Utils/isFinanceCaptive';
import type { CalculatorMessages } from '../Calculator.messages';
import { isPfs, isVwfs, mapperFinanceProducts } from '../Helpers/FinanceMap';

import styles from './ProductToggle.module.css';

export type ProductProps = {
  strings: CalculatorMessages;
};

export const ProductToggle = ({ strings }: ProductProps) => {
  const locale = useAtomValue(localeAtom);

  const eligibleProducts = useAtomValue(eligibleProductsAtom);
  const [selectedProductId, setSelectedProductId] = useAtom(selectedProductIdAtom);
  const currentProductId = useAtomValue(currentProductIdAtom);
  const [finCalcInteractedWith, setFinCalcInteractedWith] = useAtom(finCalcInteractedWithAtom);
  const calculation = useAtomValue(calculationAtom)!;
  const captiveId = calculation?.captiveId;
  const pathname = usePathname();

  const isCurrentVwfs = isVwfs(captiveId);
  const isCurrentPorsche = isPfs(captiveId);
  const hasLeasingFinancing = isLeasingFinancing(calculation);
  const hasVwfsLeasing = hasLeasingFinancing && isCurrentVwfs;

  const handleToggleChange = (productId: string) => {
    if (!finCalcInteractedWith) setFinCalcInteractedWith(true);
    setSelectedProductId(productId);
  };

  const getProductTitle = (productId: string) => {
    if (hasVwfsLeasing) return mapperFinanceProducts.vwfsPrivateLeasing?.title[locale];
    if (isCurrentPorsche && productId === 'privateLeasing') return strings.porscheLeasing;
    return mapperFinanceProducts[productId]?.title[locale];
  };

  if (pathname.includes('/finance-deal')) return null;

  const buttons =
    eligibleProducts?.map?.(productId => ({
      label: getProductTitle(productId),
      value: productId,
      dataTestId: `finance-product-${productId}`,
    })) ?? [];

  return (
    <>
      <fieldset name="finance products" className={styles.financeOptionsType}>
        <SegmentedButtons
          buttons={buttons}
          onChange={handleToggleChange}
          currentValue={selectedProductId ?? currentProductId}
        />
      </fieldset>
      <div className={styles.divider} />
    </>
  );
};
